import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { 
  collection, 
  getDocs, 
  query, 
  orderBy,
  Timestamp,
  doc,
  getDoc,
  updateDoc,
  writeBatch
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../config/firebase';
import Sidebar from '../components/Sidebar/Sidebar';
import './ReviewsPage.css';

const ReviewsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [testingAPI, setTestingAPI] = useState(false);
  const [syncingReviews, setSyncingReviews] = useState(false);
  const [generatingSuggestions, setGeneratingSuggestions] = useState(false);
  const [reviewSortMode, setReviewSortMode] = useState('waiting-first');
  const [responseTexts, setResponseTexts] = useState({});
  const [likedReviews, setLikedReviews] = useState({});

  const generateSuggestedReplyTextLocal = (review) => {
    const rating = Number(review?.rating || 0);
    const rawAuthorName = (review?.authorName || '').trim();
    const firstName = rawAuthorName ? rawAuthorName.split(' ')[0] : '';
    const greetingName = firstName || 'vous';
    const reviewText = (review?.text || '').trim().toLowerCase();

    const mentionForPositive = reviewText.includes('pizza')
      ? 'nos pizzas'
      : reviewText.includes('pate') || reviewText.includes('pâtes')
        ? 'nos pâtes'
        : reviewText.includes('service')
          ? 'notre service'
          : reviewText.includes('ambiance')
            ? 'l\'ambiance'
            : null;

    const hasComplaint =
      reviewText.includes('attente') ||
      reviewText.includes('lent') ||
      reviewText.includes('froid') ||
      reviewText.includes('retard') ||
      reviewText.includes('bruit') ||
      reviewText.includes('déçu') ||
      reviewText.includes('decu') ||
      reviewText.includes('mauvais');

    if (rating >= 4) {
      const mentionPart = mentionForPositive ? ` sur ${mentionForPositive}` : '';
      return `Merci ${greetingName} pour votre retour${mentionPart}. Toute l'équipe Aqua Farina est ravie de vous accueillir, et nous espérons vous revoir très bientôt.`;
    }

    if (rating === 3) {
      return `Merci ${greetingName} pour votre avis. Nous prenons en compte votre retour afin d'améliorer encore votre prochaine expérience chez Aqua Farina.`;
    }

    if (hasComplaint) {
      return `Merci ${greetingName} d'avoir pris le temps de partager votre retour. Nous sommes désolés pour cette expérience et nous allons faire le nécessaire avec l'équipe pour nous améliorer.`;
    }

    return `Merci ${greetingName} pour votre retour. Nous sommes désolés que l'expérience n'ait pas été à la hauteur de vos attentes et nous restons mobilisés pour nous améliorer.`;
  };

  // Vérifier l'état d'authentification et le rôle (seul role === 0 peut accéder)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        setLoading(false);
        navigate('/admin');
        return;
      }

      try {
        const userDocRef = doc(db, 'adminUsers', currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);
        const role = userDocSnap.exists() ? (userDocSnap.data().role ?? 1) : 1;
        setUserRole(role);

        if (role !== 0) {
          navigate('/admin');
          return;
        }
        loadReviews();
      } catch (error) {
        console.error('Erreur lors du chargement du rôle:', error);
        navigate('/admin');
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Fonction pour tester l'API Google My Business
  const handleTestAPI = async () => {
    if (testingAPI) return;
    
    setTestingAPI(true);
    try {
      const functions = getFunctions(app);
      const testAPI = httpsCallable(functions, 'testGoogleMyBusinessAPI');
      
      const result = await testAPI({});
      
      // Afficher les résultats dans une alerte formatée
      let message = '🔍 Résultats du test API:\n\n';
      message += `1. Access Token: ${result.data.step1_accessToken.success ? '✅' : '❌'} ${result.data.step1_accessToken.message}\n`;
      message += `2. Comptes: ${result.data.step2_accounts.success ? '✅' : '❌'} ${result.data.step2_accounts.message}\n`;
      
      // Afficher les suggestions si erreur
      if (!result.data.step2_accounts.success && result.data.step2_accounts.suggestion) {
        message += `\n💡 Suggestion: ${result.data.step2_accounts.suggestion}\n`;
      }
      
      message += `3. Locations: ${result.data.step3_locations.success ? '✅' : '❌'} ${result.data.step3_locations.message || 'Non testé'}\n`;
      message += `4. Avis: ${result.data.step4_reviews.success ? '✅' : '❌'} ${result.data.step4_reviews.message || 'Non testé'}\n`;
      
      if (result.data.step2_accounts.accountNames && result.data.step2_accounts.accountNames.length > 0) {
        message += `\n📋 Comptes trouvés (${result.data.step2_accounts.count}):\n`;
        result.data.step2_accounts.accountNames.forEach(acc => {
          message += `  - ${acc}\n`;
        });
      }
      
      if (result.data.step3_locations.locationNames && result.data.step3_locations.locationNames.length > 0) {
        message += `\n📍 Locations trouvées (${result.data.step3_locations.count}):\n`;
        result.data.step3_locations.locationNames.forEach(loc => {
          message += `  - ${loc}\n`;
        });
      }
      
      if (result.data.step4_reviews.count > 0) {
        message += `\n⭐ Avis trouvés: ${result.data.step4_reviews.count}\n`;
      }
      
      alert(message);
    } catch (error) {
      console.error('Erreur lors du test API:', error);
      alert('❌ Erreur: ' + (error.message || 'Impossible de tester l\'API'));
    } finally {
      setTestingAPI(false);
    }
  };

  const handleSyncReviews = async () => {
    if (syncingReviews) return;
    setSyncingReviews(true);
    try {
      const functions = getFunctions(app);
      const syncReviews = httpsCallable(functions, 'syncGoogleReviewsV2');
      const result = await syncReviews({});
      await loadReviews();
      alert(`✅ Synchronisation terminée\nNouveaux: ${result.data?.added || 0}\nMis à jour: ${result.data?.updated || 0}`);
    } catch (error) {
      console.error('Erreur lors de la synchronisation des avis:', error);
      alert('❌ Erreur synchronisation avis: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setSyncingReviews(false);
    }
  };

  // Générer des suggestions de réponse pour les avis en attente
  const handleGenerateSuggestions = async () => {
    if (generatingSuggestions) return;

    setGeneratingSuggestions(true);
    try {
      const batch = writeBatch(db);
      let updatedLocal = 0;
      let skipped = 0;

      reviews.forEach((review) => {
        const hasResponse = review.responseText && review.responseText.trim().length > 0;
        if (review.status !== 'waiting' || hasResponse) {
          skipped++;
          return;
        }

        const suggestion = generateSuggestedReplyTextLocal(review);
        if (!suggestion) {
          skipped++;
          return;
        }

        batch.update(doc(db, 'googleReviews', review.id), {
          suggestedResponse: suggestion,
          suggestionStatus: 'ready',
          suggestedAt: Timestamp.now(),
          suggestionVersion: 'rules-v1'
        });
        updatedLocal++;
      });

      if (updatedLocal > 0) {
        await batch.commit();
      }

      await loadReviews();
      alert(`✅ ${updatedLocal} suggestion(s) prédéfinie(s) générée(s)\n⏭️ ${skipped} avis ignoré(s)`);
    } catch (error) {
      console.error('Erreur lors de la génération des suggestions prédéfinies:', error);
      alert('❌ Erreur lors de la génération des suggestions: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setGeneratingSuggestions(false);
    }
  };

  // Charger les avis depuis Firestore
  const loadReviews = async () => {
    setLoadingReviews(true);
    try {
      const reviewsRef = collection(db, 'googleReviews');
      const q = query(reviewsRef, orderBy('time', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const reviewsData = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        reviewsData.push({
          id: doc.id,
          ...data,
          // Initialiser les états de réponse et like
          responseText: data.responseText || '',
          suggestedResponse: data.suggestedResponse || '',
          isLiked: data.isLiked || false,
          status: data.responseText ? 'responded' : 'waiting'
        });
      });
      
      setReviews(reviewsData);
      
      // Initialiser les états de réponse et like
      const responseTextsState = {};
      const likedState = {};
      reviewsData.forEach(review => {
        responseTextsState[review.id] = review.responseText || review.suggestedResponse || '';
        likedState[review.id] = review.isLiked || false;
      });
      setResponseTexts(responseTextsState);
      setLikedReviews(likedState);
    } catch (error) {
      console.error('Erreur lors du chargement des avis:', error);
    } finally {
      setLoadingReviews(false);
    }
  };

  // Toggle like
  const handleToggleLike = async (reviewId) => {
    try {
      const newLikedState = !likedReviews[reviewId];
      setLikedReviews(prev => ({ ...prev, [reviewId]: newLikedState }));
      
      const reviewRef = doc(db, 'googleReviews', reviewId);
      await updateDoc(reviewRef, {
        isLiked: newLikedState,
        likedAt: newLikedState ? Timestamp.now() : null
      });
    } catch (error) {
      console.error('Erreur lors du like:', error);
      alert('Erreur lors du like');
      // Revert state on error
      setLikedReviews(prev => ({ ...prev, [reviewId]: !prev[reviewId] }));
    }
  };

  // Soumettre la réponse et la publier sur Google Maps
  const handleSubmitResponse = async (reviewId) => {
    const responseText = responseTexts[reviewId]?.trim();
    
    if (!responseText) {
      alert('Veuillez entrer une réponse');
      return;
    }

    try {
      // Trouver l'avis pour obtenir le googleReviewId si disponible
      const review = reviews.find(r => r.id === reviewId);
      const googleReviewId = review?.googleReviewId || null;

      // Appeler la fonction Firebase pour publier la réponse sur Google Maps
      const functions = getFunctions(app);
      const respondToReview = httpsCallable(functions, 'respondToGoogleReviewV2');
      
      const result = await respondToReview({
        reviewId: reviewId,
        responseText: responseText,
        googleReviewId: googleReviewId,
        locationId: null // Sera utilisé depuis la config Firebase si non fourni
      });

      // Mettre à jour l'état local
      setReviews(prev => prev.map(review => 
        review.id === reviewId 
          ? { 
              ...review, 
              responseText, 
              status: 'responded',
              publishedOnGoogle: result.data?.published || false
            }
          : review
      ));

      if (result.data?.published) {
        alert('✅ Réponse publiée sur Google Maps avec succès !');
      } else {
        alert('⚠️ Réponse enregistrée localement. ' + (result.data?.message || 'La publication sur Google Maps nécessite une configuration supplémentaire.'));
      }
    } catch (error) {
      console.error('Erreur lors de la publication de la réponse:', error);
      
      // Si l'erreur indique que la fonction n'est pas trouvée, sauvegarder quand même dans Firestore
      if (error.code === 'functions/not-found' || error.message?.includes('NOT_FOUND')) {
        try {
          const reviewRef = doc(db, 'googleReviews', reviewId);
          await updateDoc(reviewRef, {
            responseText: responseText,
            respondedAt: Timestamp.now(),
            status: 'responded',
            publishedOnGoogle: false,
            publishError: 'Fonction Firebase non déployée'
          });

          setReviews(prev => prev.map(review => 
            review.id === reviewId 
              ? { ...review, responseText, status: 'responded' }
              : review
          ));

          alert('⚠️ Réponse enregistrée localement. Pour publier sur Google Maps, déployez la fonction Firebase.');
        } catch (firestoreError) {
          console.error('Erreur lors de la sauvegarde dans Firestore:', firestoreError);
          alert('Erreur lors de l\'enregistrement de la réponse');
        }
      } else {
        alert('Erreur lors de la publication de la réponse: ' + (error.message || 'Erreur inconnue'));
      }
    }
  };

  // Formater la date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    return new Date(timestamp).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Rendre les étoiles
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} className={`star ${i <= rating ? 'filled' : ''}`}>
          ★
        </span>
      );
    }
    return stars;
  };

  const visibleReviews = useMemo(() => {
    const list = [...reviews];

    if (reviewSortMode === 'waiting-only') {
      return list.filter((review) => review.status !== 'responded');
    }

    if (reviewSortMode === 'responded-only') {
      return list.filter((review) => review.status === 'responded');
    }

    const waitingFirst = reviewSortMode === 'waiting-first';
    list.sort((a, b) => {
      const aResponded = a.status === 'responded';
      const bResponded = b.status === 'responded';

      if (aResponded !== bResponded) {
        if (waitingFirst) {
          return aResponded ? 1 : -1;
        }
        return aResponded ? -1 : 1;
      }

      const aTime = a?.time?.seconds || 0;
      const bTime = b?.time?.seconds || 0;
      return bTime - aTime;
    });

    return list;
  }, [reviews, reviewSortMode]);

  if (loading) {
    return (
      <div className="reviews-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="reviews-page">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} userRole={userRole} />
      
      <div className="reviews-content-wrapper">
        <div className="reviews-header">
          <button 
            className="sidebar-toggle-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle sidebar"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <h1>Avis Google Maps</h1>
          <div className="reviews-header-actions">
            <select
              className="reviews-filter-select"
              value={reviewSortMode}
              onChange={(e) => setReviewSortMode(e.target.value)}
              title="Trier les avis par statut"
            >
              <option value="waiting-first">En attente d'abord</option>
              <option value="responded-first">Répondu d'abord</option>
              <option value="waiting-only">En attente uniquement</option>
              <option value="responded-only">Répondu uniquement</option>
            </select>
            <button 
              className="suggest-btn"
              onClick={handleGenerateSuggestions}
              disabled={generatingSuggestions || loadingReviews}
              title="Générer des suggestions de réponses pour les avis en attente"
            >
              {generatingSuggestions ? (
                <>
                  <span className="spinner-small"></span>
                  Génération...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 3v3m0 12v3m9-9h-3M6 12H3m15.364 6.364-2.121-2.121M8.757 8.757 6.636 6.636m11.728 0-2.121 2.121M8.757 15.243l-2.121 2.121" />
                  </svg>
                  Suggérer réponses
                </>
              )}
            </button>
            <button
              className="suggest-btn"
              onClick={handleSyncReviews}
              disabled={syncingReviews || loadingReviews}
              title="Récupérer les derniers avis Google"
            >
              {syncingReviews ? (
                <>
                  <span className="spinner-small"></span>
                  Sync...
                </>
              ) : (
                <>Récupérer avis</>
              )}
            </button>
            <button 
              className="test-api-btn"
              onClick={handleTestAPI}
              disabled={testingAPI}
              title="Tester la connexion à l'API Google My Business"
            >
              {testingAPI ? (
                <>
                  <span className="spinner-small"></span>
                  Test...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                  </svg>
                  Tester API
                </>
              )}
            </button>
          </div>
        </div>

        <div className="reviews-content">
          {loadingReviews ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Chargement des avis...</p>
            </div>
          ) : visibleReviews.length === 0 ? (
            <div className="empty-state">
              <p>Aucun avis pour ce filtre</p>
            </div>
          ) : (
            <div className="reviews-table-container">
              <table className="reviews-table">
                <thead>
                  <tr>
                    <th>Auteur</th>
                    <th>Note</th>
                    <th>Commentaire</th>
                    <th>Date</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleReviews.map((review) => (
                    <tr key={review.id}>
                      <td className="author-cell">
                        <div className="author-info">
                          {review.profilePhotoUrl && (
                            <img 
                              src={review.profilePhotoUrl} 
                              alt={review.authorName}
                              className="author-photo"
                            />
                          )}
                          <span>{review.authorName || 'Anonyme'}</span>
                        </div>
                      </td>
                      <td className="rating-cell">
                        <div className="stars-container">
                          {renderStars(review.rating || 0)}
                        </div>
                      </td>
                      <td className="text-cell">
                        {review.text ? (
                          <p className="review-text">{review.text}</p>
                        ) : (
                          <span className="no-text">Aucun commentaire</span>
                        )}
                        {review.responseText && (
                          <div className="response-container">
                            <strong>Réponse:</strong>
                            <p className="response-text">{review.responseText}</p>
                            {review.respondedAt && (
                              <span className="response-date">
                                Répondu le {formatDate(review.respondedAt)}
                                {review.publishedOnGoogle && (
                                  <span className="published-badge"> ✓ Publié sur Google Maps</span>
                                )}
                                {review.publishedOnGoogle === false && (
                                  <span className="not-published-badge"> ⚠ Non publié (config requise)</span>
                                )}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="date-cell">
                        {formatDate(review.time)}
                      </td>
                      <td className="status-cell">
                        <span className={`status-badge ${review.status || 'waiting'}`}>
                          {review.status === 'responded' ? 'Répondu' : 'En attente'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <div className="review-actions">
                          <button
                            className={`like-btn ${likedReviews[review.id] ? 'liked' : ''}`}
                            onClick={() => handleToggleLike(review.id)}
                            title={likedReviews[review.id] ? 'Retirer le like' : 'Liker'}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill={likedReviews[review.id] ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                            </svg>
                          </button>
                        </div>
                        <div className="response-section">
                          {review.suggestedResponse && !review.responseText && (
                            <div className="ai-badge">Suggestion prédéfinie</div>
                          )}
                          <textarea
                            className="response-input"
                            placeholder="Écrivez votre réponse..."
                            value={responseTexts[review.id] || ''}
                            onChange={(e) => setResponseTexts(prev => ({
                              ...prev,
                              [review.id]: e.target.value
                            }))}
                            rows="3"
                            disabled={review.status === 'responded'}
                          />
                          <button
                            className="submit-response-btn"
                            onClick={() => handleSubmitResponse(review.id)}
                            disabled={review.status === 'responded' || !responseTexts[review.id]?.trim()}
                          >
                            {review.status === 'responded' ? 'Déjà répondu' : 'Envoyer'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewsPage;

